import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/admin/AdminHeader';
import authService from '../../services/authService';
import api from '../../services/api';
import PartnerProfileTab from '../../components/admin/partner/PartnerProfileTab';
import PartnerSettingsTab from '../../components/admin/partner/PartnerSettingsTab';
import PartnerActionConfigsTab from '../../components/admin/partner/PartnerActionConfigsTab';
import PartnerActionsTab from '../../components/admin/partner/PartnerActionsTab';
import PartnerFlowConfigsTab from '../../components/admin/partner/PartnerFlowConfigsTab';
import PartnerFlowsTab from '../../components/admin/partner/PartnerFlowsTab';

function PartnerDetailPage() {
  const { partnerId } = useParams();
  const [activeTab, setActiveTab] = useState('profile');
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [flowsFilter, setFlowsFilter] = useState(null); // { type: 'config', id: '...', name: '...' }
  const [actionsFilter, setActionsFilter] = useState(null); // { type: 'config', id: '...', name: '...' }
  const navigate = useNavigate();

  useEffect(() => {
    if (!authService.isAdminLoggedIn()) {
      navigate('/admin/login');
      return;
    }

    const userData = authService.getAdminUser();
    if (!userData) {
      navigate('/admin/login');
      return;
    }

    setAdminUser(userData);
  }, [navigate]);

  useEffect(() => {
    if (adminUser && partnerId) {
      fetchPartner();
    }
  }, [adminUser, partnerId]);

  const fetchPartner = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getPartner(partnerId);
      setPartner(response.data.partner);
    } catch (err) {
      console.error('Error fetching partner:', err);
      setError('Failed to load partner details');
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerUpdate = (updatedPartner) => {
    setPartner(updatedPartner);
  };

  const handleViewActions = (configId, actionType) => {
    setActionsFilter({ type: 'config', id: configId, name: actionType });
    setActiveTab('actions');
  };

  const handleViewFlows = (configId, configName) => {
    setFlowsFilter({ type: 'config', id: configId, name: configName });
    setActiveTab('flows');
  };

  if (!adminUser) return <div>Loading...</div>;

  const tabStyle = {
    padding: '12px 24px',
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    transition: 'all 0.2s',
    backgroundColor: 'transparent'
  };

  const activeTabStyle = {
    ...tabStyle,
    borderBottomColor: '#4f46e5',
    color: '#4f46e5'
  };

  return (
    <div>
      <AdminHeader adminUser={adminUser} />
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '20px' 
      }}>
        {/* Back button */}
        <button
          onClick={() => navigate('/admin/partners')}
          style={{
            padding: '8px 16px',
            marginBottom: '20px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ← Back to Partners
        </button>

        {error && (
          <div style={{
            padding: '15px',
            marginBottom: '20px',
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            color: '#dc2626'
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            Loading partner details...
          </div>
        ) : !partner ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <p>Partner not found</p>
          </div>
        ) : (
          <>
            {/* Partner Header */}
            <div style={{
              padding: '24px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              marginBottom: '20px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#4f46e5',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '20px'
                }}>
                  <span style={{
                    color: 'white',
                    fontSize: '28px',
                    fontWeight: 'bold'
                  }}>
                    {partner.displayName?.charAt(0).toUpperCase() || 'P'}
                  </span>
                </div>
                <div>
                  <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', color: '#111827' }}>
                    {partner.displayName}
                  </h1>
                  <p style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#6b7280' }}>
                    Partner Key: <span style={{ fontFamily: 'monospace', fontWeight: '500' }}>{partner.partnerKey}</span>
                  </p>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '100px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: partner.isActive ? '#dcfce7' : '#fee2e2',
                    color: partner.isActive ? '#166534' : '#dc2626'
                  }}>
                    {partner.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px 8px 0 0',
              borderBottom: 'none',
              display: 'flex',
              gap: '8px',
              padding: '0 20px'
            }}>
              <div
                onClick={() => setActiveTab('profile')}
                style={activeTab === 'profile' ? activeTabStyle : tabStyle}
              >
                Profile
              </div>
              <div
                onClick={() => setActiveTab('settings')}
                style={activeTab === 'settings' ? activeTabStyle : tabStyle}
              >
                Settings
              </div>
              <div
                onClick={() => setActiveTab('actionConfigs')}
                style={activeTab === 'actionConfigs' ? activeTabStyle : tabStyle}
              >
                Action Configs
              </div>
              <div
                onClick={() => setActiveTab('actions')}
                style={activeTab === 'actions' ? activeTabStyle : tabStyle}
              >
                Actions
              </div>
              <div
                onClick={() => setActiveTab('flowConfigs')}
                style={activeTab === 'flowConfigs' ? activeTabStyle : tabStyle}
              >
                Flow Configs
              </div>
              <div
                onClick={() => setActiveTab('flows')}
                style={activeTab === 'flows' ? activeTabStyle : tabStyle}
              >
                Flows
              </div>
            </div>

            {/* Tab Content */}
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '0 0 8px 8px',
              padding: '24px',
              minHeight: '400px'
            }}>
              {activeTab === 'profile' && (
                <PartnerProfileTab partner={partner} />
              )}
              {activeTab === 'settings' && (
                <PartnerSettingsTab 
                  partner={partner} 
                  onUpdate={handlePartnerUpdate}
                />
              )}
              {activeTab === 'actionConfigs' && (
                <PartnerActionConfigsTab 
                  partner={partner}
                  onViewActions={handleViewActions}
                />
              )}
              {activeTab === 'actions' && (
                <PartnerActionsTab 
                  partner={partner}
                  filter={actionsFilter}
                  onClearFilter={() => setActionsFilter(null)}
                />
              )}
              {activeTab === 'flowConfigs' && (
                <PartnerFlowConfigsTab 
                  partner={partner}
                  onViewFlows={handleViewFlows}
                />
              )}
              {activeTab === 'flows' && (
                <PartnerFlowsTab 
                  partner={partner}
                  filter={flowsFilter}
                  onClearFilter={() => setFlowsFilter(null)}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PartnerDetailPage;
